(ns kyber.events.analytics
  (:require
   [goog.object :as gobj]
   [re-frame.core :as re-frame]
   [cljs-time.core :as t]
   [cljs-time.format :as f]
   [cljs.pprint :as pprint]

   [kyber.constants :refer [max-analytics-retries
                            analytics-exponential-back-off
                            analytics-polling-interval]]
   [kyber.events.utils :refer [add-auth base-uri]]
   [kyber.subscriptions.authentication :refer [session-token]]))

;; Private Helpers

(defn- clear-analytic-request
  [db request-id]
  (-> db
      (update-in [:analytics :in-flight] #(dissoc % request-id))
      (update-in [:request] #(dissoc % request-id))))

(defn- format-request-failure
  [{:keys [db]} [_
                 {:keys [retry-attempt request-id]}
                 err]]
  {:db db
   :dispatch-n [[:analytics/check-failed-request err]
                [:dispatch-in
                 (* (inc retry-attempt) analytics-exponential-back-off)
                 :analytics/retry-queue request-id retry-attempt]]})

(re-frame/reg-event-fx :analytics/format-request-failure format-request-failure)

(defn- format-request
  [{:keys [request-id db payload retry-attempt]}]
  (let [token (session-token db)]
    {:name request-id
     :method :post
     :uri (base-uri db (if (some? token)
                         "/v1/analytics/consume"
                         "/v1/analytics/consume/unauthorized"))
     :format :transit
     :response-format :json
     :headers (add-auth db)
     :params payload
     :on-success [:analytics/send-queue-success request-id]
     :on-failure [:analytics/format-request-failure
                  {:retry-attempt retry-attempt
                   :request-id request-id}]}))

(defn- assoc-acid
  "Adds the active acid to a datapoint"
  [db datapoint]
  (assoc datapoint :acid (-> db
                             (get-in [:analytics :application-context-ids])
                             last)))

(defn- add-timestamp
  "Adds a utc timestamp to the event"
  [datapoint]
  (assoc datapoint :time (f/unparse (:date-time f/formatters) (t/now))))

(defn- add-event-uuid
  "Adds an event uuid to each unique datapoint. De-duping"
  [datapoint]
  (assoc datapoint :event-uuid (str (random-uuid))))

(defn- get-expired-acids
  "Returns a set of expired acids that will no longer be in use"
  [lateset-acid all-acids]
  (->> all-acids
       (reduce (fn [{:keys [acid-found] :as acc} acid]
                 (if acid-found
                   acc
                   (if (= lateset-acid acid)
                     (assoc acc :acid-found true)
                     (update acc :expired-acids conj acid))))
               {:acid-found false
                :expired-acids #{}})
       :expired-acids))

(defn- remove-expired-acids-from-context-ids
  [expired-acids]
  (fn [acids]
    (->> acids
         (remove #(contains? expired-acids %))
         (vec))))

(defn- remove-expired-contexts
  [expired-cids]
  (fn [contexts]
    (apply dissoc contexts expired-cids)))

;; Public Events

(defn set-event-context
  [db [_ {:keys [ecid event-context]}]]
  (assoc-in db [:analytics :event-contexts ecid] event-context))

(defn remove-event-context
  [db [_ {:keys [ecid]}]]
  (update-in db [:analytics :stale-event-contexts] conj ecid))

(defn send-queue-success
  [{:keys [db]} [_ request-id]]
  {:db (clear-analytic-request db request-id)})

(defn send-queue-failure
  [{:keys [db]} [_ request-id]]
  {:db (clear-analytic-request db request-id)
   :dispatch [:report-error {:name (str "Failed to send analytics. Request ID: "
                                        "'" request-id "'")}]})

(defn retry-queue
  [{:keys [db]} [_ request-id attempt]]
  (if (<= attempt max-analytics-retries)
    {:db db
     :request (format-request {:request-id request-id
                               :db db
                               :payload (get-in db [:analytics
                                                    :in-flight
                                                    request-id])
                               :retry-attempt (inc attempt)})}
    {:db db
     :dispatch [:analytics/send-queue-failure request-id]}))

(defn send-queue
  [{:keys [db]} _]
  (if (empty? (get-in db [:analytics :queue]))
    (let [{:keys [stale-event-contexts]}
          (:analytics db)]
      {:db (-> db
               (assoc-in [:analytics :stale-event-contexts] #{})
               (update-in [:analytics :event-contexts]
                          (remove-expired-contexts stale-event-contexts)))})
    (let [{:keys [queue
                  application-context-ids
                  application-contexts
                  event-contexts
                  stale-event-contexts]}
          (:analytics db)

          request-id
          (keyword "analytics" (str "send-queue-" (.getTime (js/Date.))))

          ;; Last acid referenced in the sorted queue
          {:keys [acid]}
          (last queue)

          ;; Expired acids are acids that will no longer be required on
          ;; the next send event
          expired-acids
          (get-expired-acids acid application-context-ids)

          ;; Referenced acids are acids that are referenced in the queue
          referenced-acids
          (select-keys application-contexts (conj expired-acids acid))

          updated-db
          (-> db
              (assoc-in [:analytics :queue] [])
              (assoc-in [:analytics :stale-event-contexts] #{})
              (update-in [:analytics :application-context-ids]
                         (remove-expired-acids-from-context-ids
                          expired-acids))
              (update-in [:analytics :application-contexts]
                         (remove-expired-contexts expired-acids))
              (update-in [:analytics :event-contexts]
                         (remove-expired-contexts stale-event-contexts))
              (update-in [:analytics :in-flight]
                         #(merge % {request-id
                                    {:q queue
                                     :actx referenced-acids
                                     :ectx event-contexts}})))]

      {:db updated-db
       :request (format-request {:request-id request-id
                                 :db updated-db
                                 :payload (get-in updated-db [:analytics
                                                              :in-flight
                                                              request-id])
                                 :retry-attempt 0})})))

(defn save-datapoint
  [{:keys [debug filtered-events]}]
  (fn [db [_ {:keys [event] :as datapoint}]]
    (let [formatted-datapoint (->> (dissoc datapoint :event)
                                   (merge {:event-name (name event)
                                           :event-namespace (namespace event)})
                                   (assoc-acid db)
                                   (add-timestamp)
                                   (add-event-uuid))]

      (when debug
        (if (some? filtered-events)
          (when (contains? filtered-events event)
            (pprint/pprint formatted-datapoint))
          (pprint/pprint formatted-datapoint)))

      (update-in db [:analytics :queue] #(conj % formatted-datapoint)))))

(defn- is-auth-error?
  [err]
  (= (:status err) 401))

(defn check-failed-request
  [{:keys [db]} [_ err]]
  (cond-> {:db db}
    (is-auth-error? err)
    (assoc :dispatch-n
           [[:notifier/create-alert
             {:title "Whoops! Looks like we've lost track of who you are!"
              :message "Please refresh the page to try again!"
              :confirm-text "Refresh"
              :confirm-action #(js/location.reload)
              :hide-dismiss true
              :confirm-only true}]])))

(re-frame/reg-event-fx :analytics/check-failed-request check-failed-request)

(defn- get-browser-prefix
  []
  (let [prefixes ["webkit" "moz" "ms" "o"]
        default-hidden (when (some? (gobj/get js/document "hidden")) "")
        custom-hidden (reduce #(let [prefix (str %2 "Hidden")]
                                 (if (some? %1)
                                   %1
                                   (when (some? (gobj/get js/document prefix))
                                     %2)))
                              prefixes)]
    (or default-hidden custom-hidden)))

(defn- get-visibility-change
  []
  (str (get-browser-prefix) "visibilitychange"))

(defn- get-hidden-property
  []
  (let [browser-prefix (get-browser-prefix)]
    (if (seq browser-prefix)
      (str browser-prefix "Hidden")
      "hidden")))

(defn- is-supported-environment?
  []
  (and (some? (gobj/get js/document "addEventListener"))
       (some? (get-hidden-property))))

(when ^boolean js/goog.DEBUG
  (def event-doc-tab-unfocused
    {:description "The browser tab is out of focus"
     :changelog
     [{:date "28OCT19"
       :event-version 0
       :data {}}]}))

(when ^boolean js/goog.DEBUG
  (def event-doc-tab-refocused
    {:description "The browser tab has come back into focus"
     :changelog
     [{:date "28OCT19"
       :event-version 0
       :data {}}]}))

(defn- send-session-changed-event!
  [_]
  (let [is-hidden (gobj/get js/document (get-hidden-property))]
    (re-frame/dispatch [:analytics/save-datapoint
                        {:event (if (true? is-hidden)
                                  :analytics/tab-unfocused
                                  :analytics/tab-refocused)
                         :event-version 0}])))

(defn track-page-blur
  [should-track-page-blur]
  (when (and (true? should-track-page-blur)
             (is-supported-environment?))
    (js-invoke js/document
               "addEventListener"
               (get-visibility-change)
               send-session-changed-event!
               false)))

(defn initialize-analytics
  "Registers Kyber analytics events"
  ([] (initialize-analytics {:debug false}))
  ([{:keys [debug filtered-events]}]
   (re-frame/reg-event-db :analytics/set-event-context
                          set-event-context)

   (re-frame/reg-event-db :analytics/remove-event-context
                          remove-event-context)

   (re-frame/reg-event-fx :analytics/send-queue-success
                          send-queue-success)

   (re-frame/reg-event-fx :analytics/send-queue-failure
                          send-queue-failure)

   (re-frame/reg-event-fx :analytics/retry-queue
                          retry-queue)

   (re-frame/reg-event-fx :analytics/send-queue
                          send-queue)

   (re-frame/reg-event-db :analytics/save-datapoint
                          (save-datapoint (cond-> {:debug debug}
                                            (some? filter)
                                            (assoc :filtered-events
                                                   filtered-events))))

   (re-frame/reg-fx :analytics/track-page-blur
                    track-page-blur)

   (re-frame/reg-fx :analytics/start-polling
                    (fn [should-start-polling]
                      (when (true? should-start-polling)
                        (.setInterval
                         js/window
                         #(re-frame/dispatch [:analytics/send-queue])
                         analytics-polling-interval))))))
